/** Northstar delivery window: visitor-local dates, cutoff and optional weekend skipping. */
if (!customElements.get('sc-delivery-estimate')) {
  class ScDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.dates = this.querySelector('[data-delivery-dates]');
      this.fallback = this.querySelector('[data-delivery-fallback]');
      if (!this.dates || !this.fallback) return;

      this.events?.abort();
      this.events = new AbortController();
      const refresh = () => this.render();
      window.addEventListener('pageshow', refresh, { signal: this.events.signal });
      window.addEventListener('focus', refresh, { signal: this.events.signal });
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.render();
      }, { signal: this.events.signal });
      this.render();
    }

    disconnectedCallback() {
      this.events?.abort();
      clearTimeout(this.timer);
    }

    static addDays(date, days, skipWeekends) {
      const result = new Date(date);
      // Calendar arithmetic at noon also works across daylight-saving changes.
      result.setHours(12, 0, 0, 0);
      let remaining = days;
      while (remaining > 0) {
        result.setDate(result.getDate() + 1);
        if (!skipWeekends || (result.getDay() !== 0 && result.getDay() !== 6)) remaining -= 1;
      }
      return result;
    }

    static getWindow(now, cutoff, minDays, maxDays, skipWeekends) {
      if (!Number.isFinite(now.getTime())
        || ![cutoff, minDays, maxDays].every(Number.isInteger)
        || cutoff < 0 || cutoff > 23 || minDays < 0 || maxDays < 1
        || minDays > maxDays || maxDays > 14) return null;

      const beforeCutoff = now.getHours() < cutoff;
      let base = new Date(now);
      if (!beforeCutoff) base = this.addDays(base, 1, skipWeekends);
      if (skipWeekends && (base.getDay() === 0 || base.getDay() === 6)) {
        base = this.addDays(base, 1, skipWeekends);
      }
      return {
        beforeCutoff,
        from: this.addDays(base, minDays, skipWeekends),
        to: this.addDays(base, maxDays, skipWeekends)
      };
    }

    render() {
      clearTimeout(this.timer);
      try {
        const now = new Date();
        const cutoff = Number(this.dataset.cutoffHour);
        const deliveryWindow = ScDeliveryEstimate.getWindow(now, cutoff,
          Number(this.dataset.minDays), Number(this.dataset.maxDays),
          this.dataset.skipWeekends === 'true');
        if (!deliveryWindow) throw new Error('Invalid delivery settings');

        const template = deliveryWindow.beforeCutoff
          ? this.dataset.templateBefore : this.dataset.templateAfter;
        if (!template) throw new Error('Missing delivery text');

        const format = new Intl.DateTimeFormat(this.dataset.locale || document.documentElement.lang || 'de',
          { weekday: 'short', day: '2-digit', month: '2-digit' });
        const values = { '{cutoff}': String(cutoff), '{from}': format.format(deliveryWindow.from), '{to}': format.format(deliveryWindow.to) };
        const content = document.createDocumentFragment();
        template.split(/(\{cutoff\}|\{from\}|\{to\})/).forEach((part) => {
          if (part === '{from}' || part === '{to}') {
            const strong = document.createElement('strong');
            strong.textContent = values[part];
            content.appendChild(strong);
          } else {
            content.appendChild(document.createTextNode(part === '{cutoff}' ? values[part] : part));
          }
        });
        this.dates.replaceChildren(content);
        this.dates.hidden = false;
        this.fallback.hidden = true;

        // Refresh when the promise changes; no continuous polling or layout timer.
        const next = new Date(now);
        if (deliveryWindow.beforeCutoff) next.setHours(cutoff, 0, 0, 0);
        else next.setHours(24, 0, 0, 0);
        this.timer = setTimeout(() => this.render(), next.getTime() - now.getTime() + 100);
      } catch {
        this.dates.hidden = true;
        this.fallback.hidden = false;
      }
    }
  }
  customElements.define('sc-delivery-estimate', ScDeliveryEstimate);
}
